import { DashboardCard } from "@/components/common/DashboardCard";
import { AuditLogBox } from "@/components/common/AuditLogBox";
import { PageHeader } from "@/components/common/PageHeader";
import { StatCardConfig, StatCardGrid } from "@/components/common/StatCardGrid";
import { Box, Grid2 as Grid, Typography, Paper } from "@mui/material";
import { useEffect, useState } from "react";
import { Show, TabbedShowLayout, Tab, useRecordContext, useDataProvider } from "react-admin";
import { ObjectType, Partner, ResourceName, Role, Task, User, ValidAny } from "@/types";
import { taskRecordPath } from "./utils/taskRecordLink";
import {
  DeductibleReviewTaskAction,
  showsDeductibleReviewAction,
} from "./components/shared/DeductibleReviewTaskAction";

export const TaskShow = () => {
  return (
    <>
      <Box sx={{ display: "flex" }}>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            transition: "margin 0.3s",
          }}
        >
          <Show actions={false}>
            <Box sx={{ width: "100%", p: 5 }}>
              <PageHeader titleKey="title" />
              <TabbedShowLayout>
                <Tab label="Overview">
                  <TaskSummaryTab />
                </Tab>
                <Tab label="Details">
                  <TaskDetailsTab />
                </Tab>
                <Tab label="Audit Log">
                  <AuditLogBox tableName={ResourceName.TASKS} />
                </Tab>
              </TabbedShowLayout>
            </Box>
          </Show>
        </Box>
      </Box>
    </>
  );
};

export const TaskSummaryTab = () => {
  const taskRecord = useRecordContext<Task>();
  const dataProvider = useDataProvider();
  const [relatedRecord, setRelatedRecord] = useState<ValidAny>(null);
  const [assignedUser, setAssignedUser] = useState<User | null>(null);
  const [assignedRole, setAssignedRole] = useState<Role | null>(null);
  const [partner, setPartner] = useState<Partner | null>(null);

  // Fetch related record based on record.object_type and record.object_id
  useEffect(() => {
    if (!taskRecord?.record?.object_type || !taskRecord?.record?.object_id) return;

    const { object_type, object_id } = taskRecord.record;

    const resource = object_type;
    if (resource) {
      dataProvider
        .getOne(resource, { id: object_id })
        .then(({ data }) => setRelatedRecord(data))
        // Background enrichment: a missing/deleted related record must NOT toast the
        // viewer on page load (e.g. a task pointing at a deleted claimant). Log only.
        .catch((err) => console.error(`Failed to fetch ${resource}:`, err));
    }
  }, [taskRecord?.record?.object_type, taskRecord?.record?.object_id, dataProvider]);

  // Fetch assigned user
  useEffect(() => {
    if (!taskRecord?.assigned_user_id_ref) return;

    dataProvider
      .getOne(ResourceName.USERS, { id: taskRecord.assigned_user_id_ref })
      .then(({ data }) => setAssignedUser(data))
      .catch((err) => console.error("Failed to fetch assigned user:", err));
  }, [taskRecord?.assigned_user_id_ref, dataProvider]);

  // Fetch assigned role
  useEffect(() => {
    if (!taskRecord?.assigned_role_id_ref) return;

    dataProvider
      .getOne(ResourceName.ROLES, { id: taskRecord.assigned_role_id_ref })
      .then(({ data }) => setAssignedRole(data))
      .catch((err) => console.error("Failed to fetch assigned role:", err));
  }, [taskRecord?.assigned_role_id_ref, dataProvider]);

  // Fetch partner information
  useEffect(() => {
    if (!taskRecord?.partner_id_ref) return;

    dataProvider
      .getOne(ResourceName.PARTNERS, { id: taskRecord.partner_id_ref })
      .then(({ data }) => setPartner(data))
      .catch((err) => console.error("Failed to fetch partner:", err));
  }, [taskRecord?.partner_id_ref, dataProvider]);

  const formatObjectTypeToTitle = (objectType?: ObjectType) => {
    switch (objectType) {
      case ObjectType.CLIENTS:
        return "Client";
      case ObjectType.POLICIES:
        return "Policy";
      case ObjectType.CLAIMANTS:
        return "Claimant";
      case ObjectType.STOPLOSS_CLAIMS:
        return "Stop Loss Claim";
      case ObjectType.REIMBURSEMENTS:
        return "Reimbursement";
      case ObjectType.DOCUMENTS:
        return "Documents";
      default:
        return undefined;
    }
  };

  const getRelatedRecordDisplayName = () => {
    if (!relatedRecord) return "Loading...";

    // Try different possible name fields based on record type
    return (
      relatedRecord.name ||
      relatedRecord.title ||
      relatedRecord.client_legal_name ||
      `${relatedRecord.claimant_first_name} ${relatedRecord.claimant_last_name}` ||
      relatedRecord.aegle_id ||
      `${formatObjectTypeToTitle(taskRecord?.record?.object_type)} #${taskRecord?.record?.object_id}`
    );
  };

  const taskStats: StatCardConfig[] = [
    {
      label: "Status",
      value: taskRecord?.status || "Unknown",
    },
    {
      label: "Completed",
      value: taskRecord?.completed ? "Yes" : "No",
    },
    {
      label: "Due Date",
      value: taskRecord?.due_date ? new Date(taskRecord.due_date).toLocaleDateString() : "No due date",
    },
    {
      label: "Assignment",
      value: assignedUser ? "User" : assignedRole ? "Role" : "Unassigned",
    },
  ];

  return (
    <Box>
      <StatCardGrid cards={taskStats} />

      <Box mt={4}>
        <Grid container spacing={3}>
          {/* Task Details */}
          <Grid size={{ xs: 12, md: 6 }}>
            <DashboardCard title="Task Information">
              <Box p={2}>
                <Typography variant="body2" gutterBottom>
                  <strong>Title:</strong> {taskRecord?.title || "Unknown"}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Description:</strong> {taskRecord?.description || "No description"}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Aegle ID:</strong> {taskRecord?.aegle_id || "N/A"}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Created:</strong>{" "}
                  {taskRecord?.created_datetime ? new Date(taskRecord.created_datetime).toLocaleString() : "Unknown"}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Created By:</strong> {taskRecord?.created_by || "Unknown"}
                </Typography>
              </Box>
            </DashboardCard>
          </Grid>

          {/* Assignment Details */}
          <Grid size={{ xs: 12, md: 6 }}>
            <DashboardCard title="Assignment Details">
              <Box p={2}>
                {assignedUser && (
                  <>
                    <Typography variant="body2" gutterBottom>
                      <strong>Assigned User:</strong> {assignedUser.full_name || assignedUser.email}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>User Email:</strong> {assignedUser.email}
                    </Typography>
                  </>
                )}
                {assignedRole && (
                  <>
                    <Typography variant="body2" gutterBottom>
                      <strong>Assigned Role:</strong> {assignedRole.name}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Role Description:</strong> {assignedRole.description || "N/A"}
                    </Typography>
                  </>
                )}
                {!assignedUser && !assignedRole && (
                  <Typography variant="body2" color="text.secondary">
                    Task is not assigned to any user or role
                  </Typography>
                )}
                {partner && (
                  <Typography variant="body2" gutterBottom>
                    <strong>Partner:</strong> {partner.partner_company_name || partner.partner_code || "N/A"}
                  </Typography>
                )}
              </Box>
            </DashboardCard>
          </Grid>

          {/* Deductible review (Feature #3502): the action lives on the claimant page. */}
          {showsDeductibleReviewAction(taskRecord) && (
            <Grid size={{ xs: 12 }}>
              <DeductibleReviewTaskAction task={taskRecord} />
            </Grid>
          )}

          {/* Related Record */}
          <Grid size={{ xs: 12 }}>
            <DashboardCard title="Related Record">
              <Box p={2}>
                {taskRecord?.record?.object_type && taskRecord?.record?.object_id ? (
                  <Box
                    onClick={() => {
                      // Navigate to the related record (a deductible-review task
                      // lands on the claimant's Deductibles tab with the dialog open).
                      const path = taskRecord ? taskRecordPath(taskRecord) : undefined;
                      if (path) window.location.href = `/#${path}`;
                    }}
                    sx={{
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "action.hover",
                        borderRadius: 1,
                        p: 1,
                        m: -1,
                      },
                    }}
                  >
                    <Typography variant="body2" gutterBottom>
                      <strong>Type:</strong> {formatObjectTypeToTitle(taskRecord.record.object_type)}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>ID:</strong> {taskRecord.record.object_id}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                      <strong>Name:</strong> {getRelatedRecordDisplayName()}
                    </Typography>
                    <Typography variant="caption" color="primary">
                      Click to view record details →
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No related record
                  </Typography>
                )}
              </Box>
            </DashboardCard>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export const TaskDetailsTab = () => {
  const taskRecord = useRecordContext();

  return (
    <Box pt={2}>
      <Grid container spacing={3}>
        {/* Technical Details */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Technical Information
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Task ID:</strong> {taskRecord?.id || "Unknown"}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Aegle ID:</strong> {taskRecord?.aegle_id || "N/A"}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>User ID Ref:</strong> {taskRecord?.user_id_ref || "N/A"}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Partner ID Ref:</strong> {taskRecord?.partner_id_ref || "N/A"}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Assigned User ID:</strong> {taskRecord?.assigned_user_id_ref || "N/A"}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Assigned Role ID:</strong> {taskRecord?.assigned_role_id_ref || "N/A"}
            </Typography>
          </Paper>
        </Grid>

        {/* Audit Trail */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Audit Trail
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Created:</strong>{" "}
              {taskRecord?.created_datetime ? new Date(taskRecord.created_datetime).toLocaleString() : "Unknown"}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Created By:</strong> {taskRecord?.created_by || "Unknown"}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Modified:</strong>{" "}
              {taskRecord?.modified_datetime ? new Date(taskRecord.modified_datetime).toLocaleString() : "Not modified"}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Modified By:</strong> {taskRecord?.modified_by || "N/A"}
            </Typography>

            {taskRecord?.deleted && (
              <>
                <Typography variant="body2" gutterBottom color="error">
                  <strong>Deleted:</strong>{" "}
                  {taskRecord.deleted_datetime ? new Date(taskRecord.deleted_datetime).toLocaleString() : "Yes"}
                </Typography>
                <Typography variant="body2" gutterBottom color="error">
                  <strong>Deleted By:</strong> {taskRecord.deleted_by || "Unknown"}
                </Typography>
              </>
            )}
          </Paper>
        </Grid>

        {/* Related Record Details */}
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Related Record Details
            </Typography>
            {taskRecord?.record ? (
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="body2">
                    <strong>Object Type:</strong>
                    <br />
                    {taskRecord.record.object_type || "N/A"}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="body2">
                    <strong>Object ID:</strong>
                    <br />
                    {taskRecord.record.object_id || "N/A"}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Typography variant="body2">
                    <strong>Label:</strong>
                    <br />
                    {taskRecord.record.label || "N/A"}
                  </Typography>
                </Grid>
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No related record information available
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>{" "}
      {/* ← This closing tag was missing */}
    </Box>
  );
};
